import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "../../UI/Card.tsx";
import { SegmentedControl } from "../../UI/SegmentedControl.tsx";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  yAxisTitle,
} from "../../UI/Chart/chartStyle.ts";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import { SectionTitle } from "./SectionTitle.tsx";
import {
  NEW_USERS_BUCKET_COUNT,
  NewUsersBucket,
  newUsersBuckets,
  NewUsersGranularity,
} from "./newUsersBuckets.ts";
import { NewUsersOnDay } from "./useUserStatistics.ts";
import {
  USERS_COLOR,
  USERS_IN_PROGRESS_COLOR,
} from "./userStatisticsColors.ts";

const HOVERED_BUCKET_WASH = "#f0efec";

const GRANULARITY_OPTIONS: { value: NewUsersGranularity; label: string }[] = [
  { value: "week", label: `Last ${NEW_USERS_BUCKET_COUNT} weeks` },
  { value: "month", label: `Last ${NEW_USERS_BUCKET_COUNT} months` },
];

function describeBucket(
  bucket: NewUsersBucket,
  granularity: NewUsersGranularity,
): string {
  if (granularity === "month") return bucket.start.format("MMMM YYYY");
  const weekEnd = bucket.start.add(6, "day");
  return `${bucket.start.format("D MMM")} – ${weekEnd.format("D MMM YYYY")}`;
}

function NewUsersTooltip({
  active,
  payload,
  granularity,
}: TooltipProps<number, string> & { granularity: NewUsersGranularity }) {
  const bucket = payload?.[0]?.payload as NewUsersBucket | undefined;
  if (!active || !bucket) return null;
  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1"
      }
    >
      <span className={"font-semibold text-slate-700"}>
        {describeBucket(bucket, granularity)}
      </span>
      <span className={"text-slate-500"}>
        <span className={"font-semibold text-slate-800"}>
          {bucket.userCount}
        </span>{" "}
        new {bucket.userCount === 1 ? "user" : "users"}
        {bucket.isInProgress && " so far"}
      </span>
    </div>
  );
}

export function NewUsersChart({
  newUsersPerDay,
  today,
}: {
  newUsersPerDay: NewUsersOnDay[] | null;
  today: Dayjs;
}) {
  const [granularity, setGranularity] = useState<NewUsersGranularity>("week");
  const buckets = useMemo(
    () =>
      newUsersPerDay
        ? newUsersBuckets({ newUsersPerDay, granularity, today })
        : [],
    [newUsersPerDay, granularity, today],
  );

  return (
    <Card>
      <div className={"flex flex-wrap items-center justify-between gap-3"}>
        <SectionTitle>New users</SectionTitle>
        <SegmentedControl
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />
      </div>
      {newUsersPerDay === null ? (
        <LoadingBlock sizeClassName={"h-60 w-full"} />
      ) : (
        <div className={"flex flex-col gap-2"}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={buckets}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={GRID_COLOR} />
              <XAxis
                dataKey="label"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: AXIS_COLOR }}
                interval={"preserveStartEnd"}
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                label={yAxisTitle("Users")}
                width={48}
                tick={AXIS_TICK}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<NewUsersTooltip granularity={granularity} />}
                cursor={{ fill: HOVERED_BUCKET_WASH }}
              />
              <Bar
                dataKey="userCount"
                name="New users"
                radius={[4, 4, 0, 0]}
                maxBarSize={36}
                isAnimationActive={false}
              >
                {buckets.map((bucket) => (
                  <Cell
                    key={bucket.start.valueOf()}
                    fill={
                      bucket.isInProgress
                        ? USERS_IN_PROGRESS_COLOR
                        : USERS_COLOR
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className={"flex flex-wrap items-center gap-x-5 gap-y-2"}>
            <ChartLegendItem
              color={USERS_COLOR}
              label={"Accounts created"}
              shape={"block"}
            />
            <ChartLegendItem
              color={USERS_IN_PROGRESS_COLOR}
              label={
                granularity === "week"
                  ? "This week so far"
                  : "This month so far"
              }
              shape={"block"}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
